import { ref } from "s2-engine";

import inGameSideEffectsRefs from "@/game-lol/in-game-side-effects.refs.mjs";
import lolRefs from "@/game-lol/refs.mjs";
import { IMPORT_ENUMS } from "@/game-lol/utils/in-game-constants.mjs";
import globals from "@/util/global-whitelist.mjs";
import lruObject from "@/util/lru-object.mjs";
import { observable } from "@/util/observable.mjs";

// "options" is just state that is typically user-input via the ui
// This is a function because we need to be able to call
// it to reset back to the initial state for each consecutive game.
export const getInitialOptions = () => ({
  selectedTab: null,
  selectedTabAutoSwitched: false,
  selectedRunePageIndex: 0,
  selectedSummonerSpellPageIndex: 0,
  showBans: false,
  selectedRole: null,
  selectingRole: false,
  selectedOpponent: null,
  selectingOpponent: false,
  localBanStored: false,
  removedBannedChampions: {},

  runesManuallyChanged: false,

  dynamicBuild: false,
  damageReportTeamCopied: false,
  damageReportEnemyCopied: false,

  // Suggestions
  suggestionsSearch: "",
  suggestionsClickedSearchId: null,
  suggestionsClickedBanId: null,
  suggestionsClickedPickId: null,
  declaredLaneCounterMore: false,
  declaredGameCounterMore: false,
  teammatesCountersMore: {},
  metaBansMore: false,
  laneCounterMore: false,
  gameCounterMore: false,
  careerMore: false,
  metaPicksMore: false,
  teammatesSynergiesMore: {},

  arenaSearchSingle: "",
  arenaSearchSynergy: "",
  arenaSearchDuo: "",
});

export const inGameData = observable({
  championsStatsUpdatedAt: 0,
  championMatchupsUpdatedAt: 0,

  // TODO: store these in normal state like champ stats & matchups
  synergies: {},
  synergiesUpdatedAt: null,

  arenaMetaStats: [],
  arenaMetaDuoStats: {},
  arenaMetaStatsChampion: observable({}),
});

// "playerData" is state to hold information about the players in the game
// This is a function because we need to be able to call
// it to reset back to the initial state for each consecutive game.
export const getInitialPlayersState = () => ({
  // team agnostic
  accountsBySummonerId: {},
  accountsUpdatedAt: 0,
  summonerIdByChampionId: {},
  championIdBySummonerId: {},
  riotGameNameBySummonerId: {},
  riotTagLineBySummonerId: {},
  playStylesBySummonerId: {},
  championStatsBySummonerId: {},
  masteryBySummonerId: {},

  riotDerivedIdBySummonerId: {},
  summonerIdByGameName: {},

  customizationByInternalName: {},
  playstylesUpdatedAt: 0,
  fetchedSummoners: {},

  teamOne: {},
  teamTwo: {},
});

export const getInitialLiveClientPlayers = () => ({
  profiles: {},
  playstyles: {},
  championsStats: {},
});

// This is intended to be shared, serializable state.
// Memory is manually managed here since it's not part of the app state, so be very
// careful not to leak.
// Why it's separate from app-state:
// - `state-cleanup` does not apply here, since it's separate from routing. We are
//   breaking the pattern of only doing data-fetching from the route here. This is
//   because this has to function independently of the current route.
const inGameState = observable(
  {
    // If remote, computeds should not run locally.
    // WARNING: if this is not set for a remote client, the following can happen:
    // - the remote client makes separate requests to our backend to fetch builds,
    //   which may conflict with what the other client sees.
    isRemote: false,

    // This is meant to contain only data that comes directly from the LCU.
    currentState: null,
    liveClientState: null,

    gameInfo: { gameId: null, gameStartTime: 0 },
    currentMvp: { cellId: null, score: 0 },
    region: null,
    queueId: 420,
    mapId: 11,
    gameId: null,
    localSummoner: null,
    rerollsRemaining: 0,

    stagingRunes: [],

    bansUpdatedAt: null,
    lobbyMembersByCellId: {},
    localPlayerPersistedBans: {},
    pickableChampionIds: {},
    subsetChampionList: null,

    swiftplayChampion1Id: 0,
    swiftplayChampion1Role: null,
    swiftplayChampion2Id: 0,
    swiftplayChampion2Role: null,

    roleLessSwiftplayChampion1Id: 0,

    arenaFirstPlaceChampions: {},
    arenaCrowdFavorites: {},

    options: getInitialOptions(),
    playersState: getInitialPlayersState(),
    liveClientPlayers: getInitialLiveClientPlayers(),

    // Things in here take advantage of DB read/write.
    lolGameStatePersistedKeys: {
      matchupBuilds: lruObject(ref({}), 80),
      playstyleBuilds: lruObject(ref({}), 80),
      proBuilds: lruObject(ref({}), 50),
      competitiveBuilds: lruObject(ref({}), 20),
      arenaBuilds: lruObject(ref({}), 20),
      swiftplayBuilds: lruObject(ref({}), 40),
      roleLessSwiftplayBuilds: lruObject(ref({}), 40),
    },
    playstyleBuildsError: false,
    proBuildsError: false,
    roleBuildsLoading: false,
    matchupBuildsLoading: false,
    selectedBuildKey: null,

    runesImportStatus: IMPORT_ENUMS.WONT,
    spellsImportStatus: IMPORT_ENUMS.WONT,
    itemsImportStatus: IMPORT_ENUMS.WONT,

    aramMayhemAugments: {},

    settings: {},
    settingsLCU: {},

    // Real-time stuff
    rtChannels: {},

    // This is set externally when static data loads...
    isStaticDataLoaded: false,
  },
  true,
);

// Swiftplay
export const memoImportStateSP1 = {
  buildKey: "",
};
export const memoImportStateSP2 = {
  buildKey: "",
};
export const memoImportStateRLSP = {
  buildKey: "",
};

lolRefs.inGameState = inGameState;
inGameSideEffectsRefs.inGameState = inGameState;
inGameSideEffectsRefs.inGameData = inGameData;
inGameSideEffectsRefs.features = lolRefs.inGameFeatures;

globals.__BLITZ_DEV__.lolGameState = inGameState;

export default inGameState;
